import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, SlidersHorizontal, Sparkles, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxzdW5saXQlMjBicmlnaHQlMjBjbGVhbiUyMGx1eHVyeSUyMGludGVyaW9yJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3OTMxODYyMzF8MA&ixlib=rb-4.1.0&q=80&w=1920";

const LINES = ["L'exigence du propre.", "Le souci du détail."];

const ENGAGEMENTS = [
  { icon: SlidersHorizontal, label: "Prestations adaptées", testid: "engagement-adaptees" },
  { icon: Sparkles, label: "Travail soigné", testid: "engagement-soigne" },
  { icon: MapPin, label: "Proximité locale", testid: "engagement-proximite" },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="accueil" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <div className="hero-zoom absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Intérieur parfaitement entretenu par DIMORA"
            className="w-full h-[115%] object-cover object-center"
            fetchpriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-[#0A0D14]/50" />
        <div className="absolute inset-0 hero-mesh" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0A0D14] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          data-testid="hero-surtitle"
          className="inline-flex items-center gap-3 mb-10 text-xs uppercase tracking-[0.3em] font-semibold text-amber-300"
        >
          <span className="h-px w-10 bg-amber-400/70" />
          Professionnels & particuliers — Gap et alentours
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-white max-w-4xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
        >
          DIMORA accompagne les particuliers et les professionnels dans l'entretien de leurs espaces.
          Pour les particuliers : ménage de logements, vitres et remise en état.
          Pour les professionnels : entretien de bureaux et de locaux, en intervention régulière ou ponctuelle.
          Dans les Hautes-Alpes (05) et les Alpes-de-Haute-Provence (04).
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#devis"
            data-testid="hero-devis-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:bg-amber-300 hover:scale-[1.03] active:scale-[0.97] hover:shadow-[0_0_40px_rgba(242,193,78,0.5)]"
          >
            Demander un devis
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-cta-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-all duration-300 hover:border-amber-300/60 hover:text-amber-200"
          >
            Découvrir nos services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-16 flex flex-wrap gap-x-10 gap-y-5 border-t border-white/10 pt-8 max-w-2xl"
          data-testid="hero-engagements"
        >
          {ENGAGEMENTS.map((e) => (
            <div key={e.label} data-testid={e.testid} className="flex items-center gap-3">
              <e.icon className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
              <span className="text-sm font-medium tracking-wide text-slate-200">{e.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
